import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { fetchArticle } from "../lib/api";
import { useSEO, useJsonLd, SITE } from "../lib/seo";

const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
};

// Lightweight markdown renderer for h2/h3, paragraphs, lists, bold
const renderMarkdown = (md) => {
    if (!md) return null;
    const lines = md.split("\n");
    const blocks = [];
    let paragraph = [];
    let list = [];

    const flushParagraph = () => {
        if (paragraph.length) {
            blocks.push({ type: "p", text: paragraph.join(" ") });
            paragraph = [];
        }
    };
    const flushList = () => {
        if (list.length) {
            blocks.push({ type: "ul", items: list });
            list = [];
        }
    };

    for (const raw of lines) {
        const line = raw.trim();
        if (!line) {
            flushParagraph();
            flushList();
            continue;
        }
        if (line.startsWith("### ")) {
            flushParagraph(); flushList();
            blocks.push({ type: "h3", text: line.slice(4) });
        } else if (line.startsWith("## ")) {
            flushParagraph(); flushList();
            blocks.push({ type: "h2", text: line.slice(3) });
        } else if (line.startsWith("# ")) {
            flushParagraph(); flushList();
            blocks.push({ type: "h2", text: line.slice(2) });
        } else if (line.startsWith("- ") || line.startsWith("* ")) {
            flushParagraph();
            list.push(line.slice(2));
        } else {
            flushList();
            paragraph.push(line);
        }
    }
    flushParagraph();
    flushList();

    const renderInline = (text) => {
        // bold **text**
        const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
        return parts.map((p, i) => {
            if (p.startsWith("**") && p.endsWith("**")) return <strong key={i}>{p.slice(2, -2)}</strong>;
            if (p.startsWith("*") && p.endsWith("*")) return <em key={i}>{p.slice(1, -1)}</em>;
            return <React.Fragment key={i}>{p}</React.Fragment>;
        });
    };

    return blocks.map((b, idx) => {
        if (b.type === "h2") return <h2 key={idx}>{renderInline(b.text)}</h2>;
        if (b.type === "h3") return <h3 key={idx}>{renderInline(b.text)}</h3>;
        if (b.type === "ul") return (
            <ul key={idx}>{b.items.map((it, i) => <li key={i}>{renderInline(it)}</li>)}</ul>
        );
        return <p key={idx}>{renderInline(b.text)}</p>;
    });
};

export default function ArticlePage() {
    const { slug } = useParams();
    const [article, setArticle] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useSEO({
        title: article ? article.titolo : "Articolo",
        description: article ? article.sommario : "Approfondimento di diritto del lavoro a cura dell'Avv. Marco Candido.",
        path: `/articolo/${slug}`,
    });

    // JSON-LD: Article + BreadcrumbList schemas (rich snippets, Discover, Google News)
    useJsonLd(
        article
            ? [
                {
                    "@context": "https://schema.org",
                    "@type": "Article",
                    "headline": article.titolo,
                    "description": article.sommario,
                    "articleSection": article.categoria || "Diritto del Lavoro",
                    "inLanguage": "it-IT",
                    "datePublished": article.published_at,
                    "dateModified": article.updated_at || article.published_at,
                    "wordCount": (article.contenuto || "").split(/\s+/).filter(Boolean).length,
                    "timeRequired": `PT${article.minuti_lettura || 5}M`,
                    "author": {
                        "@type": "Person",
                        "name": "Marco Candido",
                        "jobTitle": "Avvocato",
                        "url": `${SITE.url}/`,
                    },
                    "publisher": {
                        "@type": "Organization",
                        "name": "Studio Legale Avv. Marco Candido",
                        "url": `${SITE.url}/`,
                        "logo": {
                            "@type": "ImageObject",
                            "url": "https://static.prod-images.emergentagent.com/jobs/a9778dd3-cc77-4862-94cf-6a30e83d1c8c/images/92649c30fb8195c6e2cf37183ce091f5570bd6f52a7e5b54ec32fe7a1d139edb.png",
                            "width": 1200,
                            "height": 630,
                        },
                    },
                    "image": [
                        {
                            "@type": "ImageObject",
                            "url": "https://static.prod-images.emergentagent.com/jobs/a9778dd3-cc77-4862-94cf-6a30e83d1c8c/images/92649c30fb8195c6e2cf37183ce091f5570bd6f52a7e5b54ec32fe7a1d139edb.png",
                            "width": 1200,
                            "height": 630,
                        },
                    ],
                    "mainEntityOfPage": {
                        "@type": "WebPage",
                        "@id": `${SITE.url}/articolo/${slug}`,
                    },
                    "url": `${SITE.url}/articolo/${slug}`,
                    "keywords": ["diritto del lavoro", "avvocato giuslavorista", article.categoria].filter(Boolean).join(", "),
                    "about": { "@id": `${SITE.url}/#legalservice` },
                },
                {
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                        { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE.url}/` },
                        { "@type": "ListItem", "position": 2, "name": "Articoli", "item": `${SITE.url}/#articoli` },
                        { "@type": "ListItem", "position": 3, "name": article.titolo, "item": `${SITE.url}/articolo/${slug}` },
                    ],
                },
            ]
            : null,
        "article-jsonld",
    );

    useEffect(() => {
        window.scrollTo(0, 0);
        let mounted = true;
        fetchArticle(slug)
            .then((data) => { if (mounted) { setArticle(data); setLoading(false); } })
            .catch((err) => {
                console.error(err);
                if (mounted) {
                    setError(err?.response?.status === 404 ? "Articolo non trovato." : "Errore nel caricamento dell'articolo.");
                    setLoading(false);
                }
            });
        return () => { mounted = false; };
    }, [slug]);

    return (
        <div data-testid="article-page" className="min-h-screen bg-white">
            <Header />

            <main className="pt-32 pb-20">
                <div className="max-w-3xl mx-auto px-6 lg:px-10">
                    <Link
                        to="/#articoli"
                        data-testid="back-to-articles"
                        className="inline-flex items-center gap-2 text-sm text-navy hover:text-gold-deep mb-12 transition-colors"
                    >
                        <ArrowLeft size={16} />
                        Torna agli articoli
                    </Link>

                    {loading && (
                        <div className="py-20 text-center text-slate-500 font-mono text-sm">Caricamento articolo...</div>
                    )}

                    {error && (
                        <div className="py-20 text-center text-red-600 font-serif text-xl" data-testid="article-error">{error}</div>
                    )}

                    {article && (
                        <article>
                            <div className="flex items-center gap-3 mb-6">
                                <span className="inline-block px-3 py-1 border border-gold text-gold-deep text-[10px] uppercase tracking-[0.25em] font-bold">
                                    {article.categoria}
                                </span>
                                <span className="gold-rule" />
                            </div>

                            <h1 className="font-serif text-navy text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-8">
                                {article.titolo}
                            </h1>

                            <div className="flex items-center gap-6 text-sm text-slate-500 mb-12 pb-12 border-b border-slate-200">
                                <span className="flex items-center gap-2">
                                    <Calendar size={14} className="text-gold-deep" />
                                    {formatDate(article.published_at)}
                                </span>
                                <span className="flex items-center gap-2">
                                    <Clock size={14} className="text-gold-deep" />
                                    {article.minuti_lettura} min di lettura
                                </span>
                            </div>

                            <p className="font-serif italic text-xl md:text-2xl text-navy leading-snug mb-12 border-l-2 border-gold pl-6">
                                {article.sommario}
                            </p>

                            <div className="article-content" data-testid="article-content">
                                {renderMarkdown(article.contenuto)}
                            </div>

                            <div className="mt-20 p-8 border border-gold/40 bg-[#FAFAFA]">
                                <div className="label-eyebrow mb-3">Hai bisogno di assistenza?</div>
                                <h3 className="font-serif text-navy text-2xl mb-3">Contatta lo Studio per una valutazione del Tuo caso</h3>
                                <p className="text-slate-600 text-sm mb-5">Le informazioni di questo articolo hanno natura meramente divulgativa. Per una consulenza personalizzata, contatta lo Studio.</p>
                                <Link to="/#contatti" className="btn-gold" data-testid="article-cta-contact">
                                    Richiedi un appuntamento
                                </Link>
                            </div>
                        </article>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
}
